/**
 * @file: useOrderForm.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { validateVin } from '@euler/app/flows/order/functions/vinHelper';
import {
  estimatedFinishTimeValue,
  requireReasonForEstimatedFinishTimeChange,
} from '@euler/app/flows/order/pages/OrderScreen/functions/estimateFinishTimeUtil';
import { useFormController } from '@euler/lib/form/controller';
import { StoreConfiguration } from '@euler/model';
import {
  OrgUserRoleType,
  ServiceSubjectType,
  SiteInspectionType,
} from '@euler/model/enum';
import { TaskBasicInfo } from '@euler/model/task-detail';
import { parseTagsFromString } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import { isMobilePhone, isValidLicensePlateNo } from '@euler/utils/validators';
import { isValid as isValidDateTime } from 'date-fns';
import { Platform } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import {
  ExtendedPropertyValidationErrorInfo,
  OrderExtra,
  OrderForm,
  OrderFormMode,
  OrderFormType,
} from '../types';

export function useOrderFormController({
  type,
  defaultValues,
  basicInfo,
  storeConfiguration,
  orderDetail$,
  getExtendedPropertiesValidationErrors,
}: {
  mode: OrderFormMode;
  type: OrderFormType;
  defaultValues?: Partial<OrderForm>;
  basicInfo: TaskBasicInfo | undefined;
  storeConfiguration: StoreConfiguration;
  orderDetail$: BehaviorSubject<OrderExtra>;
  getExtendedPropertiesValidationErrors: () =>
    | ExtendedPropertyValidationErrorInfo
    | undefined;
}) {
  const { user } = useCurrentUserDangerously();

  const fieldsConfig = storeConfiguration.app?.orderFlow?.fields;

  return useFormController<OrderForm>({
    licensePlateNo: {
      required: type === 'main' ? '车牌号不能为空' : false,
      defaultValue:
        defaultValues?.licensePlateNo ??
        (basicInfo != null ? basicInfo.licensePlateNo : ''),
    },
    vin: {
      defaultValue:
        defaultValues?.vin ??
        (basicInfo != null
          ? // A non-standard vehicle stores the placeholder, which is not a
            // number anyone typed and must not show up in the field.
            basicInfo.chassisNo
            ? ''
            : basicInfo.vin
          : fieldsConfig?.vin?.defaultValue ?? ''),
      // A vehicle declared non-standard is identified by its chassis number,
      // so whatever sits in this field is not submitted and not checked.
      validators:
        type === 'main'
          ? [
              (value, state) =>
                state.isNonStandardVin.value
                  ? true
                  : !value?.trim()
                  ? { error: '车架号不能为空' }
                  : validateVin(value),
            ]
          : undefined,
    },
    chassisNo: {
      defaultValue:
        defaultValues?.chassisNo ?? basicInfo?.chassisNo ?? undefined,
      // Not `required`: that flag only carries its own message when it is a
      // literal string, and this one has to be decided from another field.
      validators: [
        (value, state) =>
          !state.isNonStandardVin.value || value?.trim()
            ? true
            : { error: '车架/地盘号不能为空' },
      ],
    },
    isNonStandardVin: {
      defaultValue:
        defaultValues?.isNonStandardVin ?? Boolean(basicInfo?.chassisNo),
    },
    mileage: {
      required:
        fieldsConfig?.mileage?.required === false || type === 'child'
          ? false
          : '请输入当前行驶里程数',
      defaultValue:
        defaultValues?.mileage ??
        (basicInfo != null
          ? basicInfo.vehicleMileage
          : fieldsConfig?.mileage?.defaultValue ?? 0),
      validators: [
        value => {
          if (type === 'child') return true;
          if (value < 0) {
            return { error: '里程数不能为负数' };
          }
          if (value >= 99_999_999) {
            return { error: '里程数过大' };
          }
          return true;
        },
      ],
      empty: x =>
        (fieldsConfig?.mileage?.defaultValue != null &&
          !x &&
          x !== fieldsConfig?.mileage.defaultValue) ||
        (fieldsConfig?.mileage?.defaultValue == null && !x),
    },
    serviceAgentInfo: {
      required: '请选择服务顾问',
      defaultValue:
        defaultValues?.serviceAgentInfo ??
        (basicInfo != null
          ? {
              serviceAgentId: basicInfo.serviceAgentId,
              name: basicInfo.serviceAgentName,
            }
          : user.role?.includes(OrgUserRoleType.ServiceAgents)
          ? {
              serviceAgentId: user.memberId,
              name: user.name,
            }
          : undefined),
    },
    dashboardImgUrl: {
      required:
        fieldsConfig?.dashboardImage?.required === false || type === 'child'
          ? false
          : basicInfo != null
          ? false
          : '请拍摄仪表盘照片',
      defaultValue:
        defaultValues?.dashboardImgUrl ??
        basicInfo?.dashboardImageUrl ??
        undefined,
    },
    vehicleInfo: {
      required: type === 'main' ? '车辆信息不能为空' : false,
      defaultValue:
        defaultValues?.vehicleInfo ??
        (basicInfo != null
          ? {
              vehicleBrandName: basicInfo.vehicleBrandName,
              vehicleBrandLogo: basicInfo.vehicleBrandLogo,
              vehicleName: basicInfo.vehicleName,
              vehicleDisplacement: basicInfo.vehicleDisplacement,
              vehicleFuelType: basicInfo.vehicleFuelType,
              vehicleImageUrl: basicInfo.vehicleImageUrl,
              vehicleManufacturer: basicInfo.vehicleManufacturer,
              vehicleModel: basicInfo.vehicleModel,
              vehicleModelYear: basicInfo.vehicleModelYear,
            }
          : undefined),
    },
    remark: { defaultValue: defaultValues?.remark ?? basicInfo?.remark ?? '' },
    estimatedFinishTime: {
      required: fieldsConfig?.estimatedFinishTime?.required,
      defaultValue:
        defaultValues?.estimatedFinishTime ??
        (basicInfo?.estimatedFinishTime != null
          ? dateFromValue(basicInfo.estimatedFinishTime)
          : undefined),
      validators: [
        (value, values) => {
          if (Platform.OS === 'web' && !isValidDateTime(dateFromValue(value))) {
            return { error: '格式不正确' };
          }
          const originalValue = estimatedFinishTimeValue(
            basicInfo?.estimatedFinishTime,
          );
          const currentValue = estimatedFinishTimeValue(value);
          if (
            basicInfo != null &&
            storeConfiguration.requireReasonForUpdateOrderEstimatedFinishTime &&
            requireReasonForEstimatedFinishTimeChange(
              currentValue,
              originalValue,
            ) &&
            !values.estimatedFinishTimeUpdateReason.value
          ) {
            return { error: '请填写修改原因' };
          }
          return true;
        },
      ],
    },
    estimatedFinishTimeUpdateReason: {
      defaultValue: undefined,
    },
    estimatedFinishTimeUpdateRemark: {
      defaultValue: undefined,
    },
    tags: {
      defaultValue:
        defaultValues?.tags ??
        (basicInfo != null
          ? basicInfo.tags
          : parseTagsFromString(fieldsConfig?.tags?.defaultValue ?? '')),
      validators: [
        value => {
          if (fieldsConfig?.tags?.required !== false && !value?.length) {
            return { error: '请选择业务类型' };
          }
          return true;
        },
      ],
    },
    customerName: {
      required:
        fieldsConfig?.customerName?.required && type === 'main'
          ? '请输入客户名称'
          : false,
      defaultValue:
        defaultValues?.customerName ??
        (basicInfo != null
          ? basicInfo.customerName
          : fieldsConfig?.customerName?.defaultValue),
    },
    customerMobile: {
      required:
        fieldsConfig?.customerName?.required && type === 'main'
          ? '请输入客户名称'
          : false,
      defaultValue:
        defaultValues?.customerMobile ??
        (basicInfo != null
          ? basicInfo.customerMobile
          : fieldsConfig?.customerMobile?.defaultValue),
      validators: [
        value => {
          if (value && !isMobilePhone(value)) {
            return { error: '手机号码格式不正确' };
          }
          return true;
        },
      ],
    },
    customerType: {
      required: fieldsConfig?.customerType?.required && type === 'main',
      defaultValue:
        defaultValues?.customerType ??
        (basicInfo != null
          ? basicInfo.customerType
          : fieldsConfig?.customerType?.defaultValue),
    },
    carwashType: {
      required: fieldsConfig?.carwashType?.required && type === 'main',
      defaultValue:
        defaultValues?.carwashType ??
        (basicInfo != null
          ? basicInfo.carwashType
          : fieldsConfig?.carwashType?.defaultValue),
    },
    returnType: {
      required: fieldsConfig?.returnType?.required && type === 'main',
      defaultValue:
        defaultValues?.returnType ??
        (basicInfo != null
          ? basicInfo.returnType
          : fieldsConfig?.returnType?.defaultValue),
    },
    oldPartDisposalType: {
      required: fieldsConfig?.oldPartDisposalType?.required && type === 'main',
      defaultValue:
        defaultValues?.oldPartDisposalType ??
        (basicInfo != null
          ? basicInfo.oldPartDisposalType
          : fieldsConfig?.oldPartDisposalType?.defaultValue),
    },
    reservationType: {
      required: fieldsConfig?.reservationType?.required && type === 'main',
      defaultValue:
        defaultValues?.reservationType ??
        (basicInfo != null
          ? basicInfo.reservationType
          : fieldsConfig?.reservationType?.defaultValue),
    },
    reservationId: {
      required: false,
      defaultValue: defaultValues?.reservationId ?? undefined,
    },
    insuranceExpiryDate: {
      required: fieldsConfig?.insuranceExpiryDate?.required && type === 'main',
      defaultValue:
        defaultValues?.insuranceExpiryDate ??
        (basicInfo?.insuranceExpiryDate != null
          ? dateFromValue(basicInfo.insuranceExpiryDate)
          : undefined),
      validators:
        Platform.OS === 'web'
          ? [
              value => {
                if (!isValidDateTime(dateFromValue(value))) {
                  return { error: '格式不正确' };
                }
                return true;
              },
            ]
          : undefined,
    },
    isSpecialLicensePlateNo: {
      defaultValue:
        defaultValues?.isSpecialLicensePlateNo ??
        (basicInfo?.licensePlateNo
          ? !isValidLicensePlateNo(basicInfo.licensePlateNo)
          : false),
    },
    extendedProperties: {
      defaultValue: undefined,
      validators: [
        () => {
          const errors = getExtendedPropertiesValidationErrors();
          if (errors) {
            return { error: errors };
          }
          return true;
        },
      ],
    },
    serviceSubjects: {
      defaultValue: undefined,
      validators: [
        () => {
          if (type === 'main') {
            if (
              fieldsConfig?.serviceSubjects?.required &&
              !orderDetail$.value?.serviceSubjects?.length
            ) {
              return {
                error: '请至少选择一个服务项目',
              };
            }
            if (
              orderDetail$.value.serviceSubjects?.some(
                x =>
                  x.type === ServiceSubjectType.Inspection && !x.assignedTo?.id,
              )
            ) {
              return {
                error: '请为检测项目派工',
              };
            }
          }
          return true;
        },
      ],
    },
    facadeInspectionResults: {
      defaultValue: undefined,
      validators: [
        () => {
          const mediaCount = orderDetail$.value.preInspection
            ?.filter(x => x.inspectionType === SiteInspectionType.Facade)
            .flatMap(x =>
              x.items.flatMap(t =>
                t.medias.filter(m => m._source.type === 'remote'),
              ),
            ).length;
          if (
            type === 'main' &&
            fieldsConfig?.facadeInspection?.required &&
            !mediaCount
          ) {
            return {
              error: '请添加接车记录和外观检查',
            };
          }
          return true;
        },
      ],
    },
  });
}
